import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, FileText, AlertCircle, Loader2 } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public convention-signing page used by the company representative after
 * receiving a signed link. No authentication required — validates via the
 * one-time token. Mirrors QuoteSignPublic.jsx structurally.
 */
export default function ConventionSignPublic() {
  const { token } = useParams();
  const [conv, setConv] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/conventions/${token}`)
      .then(({ data }) => {
        setConv(data);
        if (data.signed_at) setSigned(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const onSign = async (dataUrl, name) => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/conventions/${token}/sign`, {
        signatory_name: name,
        signature_b64: dataUrl,
      });
      setSigned(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la signature');
    }
    setSubmitting(false);
  };

  const fmtEur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  if (loading) return <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0F766E]" /></div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center p-6">
        <div className="soft-card p-8 max-w-md text-center" data-testid="convention-sign-error">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
          <h1 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Lien non valide</h1>
          <p className="text-[13px] text-[#6B6C85]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFE] py-10 px-4" data-testid="convention-sign-page">
      <div className="max-w-3xl mx-auto">
        <div className="soft-card p-8 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center"><FileText className="w-6 h-6" /></div>
            <div>
              <div className="text-[12px] uppercase tracking-wide text-[#6B6C85] font-semibold">Convention de Formation</div>
              <h1 className="text-[20px] font-bold text-[#1A1B2E]">{conv.title || 'Session de formation'}</h1>
              {/* Une convention se signe des deux côtés. Le lien dit lequel :
                  sans ça, l'organisme signerait dans la case du client. */}
              {conv.partie === 'organisme' && (
                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] text-[11px] font-bold">
                  Contresignature — {conv.org?.name || "l'organisme de formation"}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-[13px]">
            <div>
              <div className="text-[#6B6C85] mb-0.5">Entreprise bénéficiaire</div>
              <div className="font-semibold text-[#1A1B2E]">{conv.client || '—'}</div>
            </div>
            <div>
              <div className="text-[#6B6C85] mb-0.5">Formateur</div>
              <div className="font-semibold text-[#1A1B2E]">{conv.trainer || '—'}</div>
            </div>
            <div>
              <div className="text-[#6B6C85] mb-0.5">Dates</div>
              <div className="font-semibold text-[#1A1B2E]">{fmtDate(conv.startDate)} → {fmtDate(conv.endDate)}</div>
            </div>
            <div>
              <div className="text-[#6B6C85] mb-0.5">Durée</div>
              <div className="font-semibold text-[#1A1B2E]">{conv.duration || 0} h</div>
            </div>
            <div className="col-span-2">
              <div className="text-[#6B6C85] mb-0.5">Lieu</div>
              <div className="font-semibold text-[#1A1B2E]">{conv.location || '—'}</div>
            </div>
            {(conv.amount_ht || conv.amount_ttc) ? (
              <>
                <div>
                  <div className="text-[#6B6C85] mb-0.5">Montant HT</div>
                  <div className="font-bold text-[#0F766E] text-[15px]">{fmtEur(conv.amount_ht)}</div>
                </div>
                <div>
                  <div className="text-[#6B6C85] mb-0.5">Montant TTC</div>
                  <div className="font-bold text-[#0F766E] text-[15px]">{fmtEur(conv.amount_ttc)}</div>
                </div>
              </>
            ) : null}
          </div>
          {conv.autre_partie_signee && (
            <div className="mt-4 text-[12.5px] text-[#0F766E] bg-[#E6FBF7] rounded-lg px-3 py-2">
              {conv.partie === 'organisme'
                ? <>L'entreprise bénéficiaire a signé le <strong>{new Date(conv.autre_partie_signee).toLocaleDateString('fr-FR')}</strong>.</>
                : <>L'organisme de formation a signé le <strong>{new Date(conv.autre_partie_signee).toLocaleDateString('fr-FR')}</strong>.</>}
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-[#EFF0F6]">
            <a
              href={`${BACKEND}/api/public/conventions/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-[12.5px] text-[#0F766E] hover:underline font-semibold"
              data-testid="convention-sign-view-pdf"
            >
              📄 Consulter le PDF complet de la convention →
            </a>
          </div>
        </div>

        {signed ? (
          <div className="soft-card p-8 text-center" data-testid="convention-sign-success">
            <CheckCircle2 className="w-12 h-12 text-[#0F766E] mx-auto mb-3" />
            <h2 className="text-[18px] font-bold text-[#1A1B2E] mb-1">
              {conv.partie === 'organisme' ? 'Convention contresignée ✓' : 'Convention signée ✓'}
            </h2>
            <p className="text-[13px] text-[#6B6C85]">
              {conv.signatory_name && <>Signée par <strong>{conv.signatory_name}</strong> le {conv.signed_at ? new Date(conv.signed_at).toLocaleString('fr-FR') : ''}.</>}
              {!conv.signatory_name && 'Merci, votre signature a bien été enregistrée.'}
            </p>
          </div>
        ) : (
          <div className="soft-card p-8" data-testid="convention-sign-pad-wrap">
            <h2 className="text-[16px] font-bold text-[#1A1B2E] mb-1">
              {conv.partie === 'organisme' ? 'Contresigner la convention' : 'Signer la convention'}
            </h2>
            <p className="text-[12px] text-[#6B6C85] mb-4">
              {conv.partie === 'organisme'
                ? "En signant ci-dessous, vous signez cette convention au nom de l'organisme de formation."
                : 'En signant ci-dessous, vous acceptez les termes de la convention de formation ci-dessus.'}
            </p>
            <SignaturePad initialName={conv.signataire_attendu || conv.client || ''} onSave={onSign} onCancel={() => {}} />
            {submitting && <div className="mt-2 text-[12px] text-[#0F766E] flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</div>}
          </div>
        )}
      </div>
    </div>
  );
}
